<template>
    <div>
        <p>
            <span>
                {{ editing 
                    ? __('You are currently editing this alert') 
                    : __("Here you can create rules so that you are alerted whenever specific reviews appear") 
                }}
            </span>
        </p>
        <ul v-if="formErrors.length > 0">
            <li class="text-danger" v-for="error in formErrors" :key="error">
                {{ error }}
            </li>
        </ul>
        <div class="row border border-rounded pill-shadow mb-3 mx-0 px-3">
            <div
                class="custom-control col-md-6 col-12 custom-switch d-flex align-items-center"
            >
                <input
                    type="checkbox"
                    v-model="filterBySentiment"
                    class="custom-control-input"
                    id="customSentimentSwitch"
                />
                <label
                    class="custom-control-label w-100 cursor-pointer"
                    for="customSentimentSwitch"
                    >{{ __("Filter by sentiment group") }}</label
                >
            </div>
            <div class="col-md-6 col-3 text-right" v-if="filterBySentiment">
                <select v-model="selectedSentimentGroup">
                    <option
                        :value="group"
                        v-for="group in sentimentGroups"
                        :key="group"
                    >
                        {{ __(group) }}
                    </option>
                </select>
            </div>
            <div class="col-md-12 pt-2" v-if="filterBySentiment">
                <p class="readable-text mb-0">
                    {{
                        __(
                            "By selecting a sentiment group, only reviews that contain mentions regarding that group will be matched."
                        )
                    }}
                </p>
            </div>
        </div>
        <div class="row border border-rounded pill-shadow mb-3 mx-0 px-3">
            <div
                class="custom-control col-md-6 custom-switch d-flex align-items-center"
            >
                <input
                    type="checkbox"
                    v-model="filterBySource"
                    class="custom-control-input"
                    id="customSwitches"
                />
                <label
                    class="custom-control-label w-100 cursor-pointer"
                    for="customSwitches"
                    >{{ __("Filter by review channel") }}</label
                >
            </div>
            <div class="col-md-6" v-if="filterBySource">
                <element-selector
                    :title="'source'"
                    :elements="availableChannels"
                    :initialSelected="selectedChannels" 
                    v-model="selectedChannels"
                />
            </div>
        </div>
        <div class="row border border-rounded pill-shadow mb-3 mx-0 px-3">
            <div
                class="custom-control col-md-6 custom-switch d-flex align-items-center"
            >
                <input
                    type="checkbox"
                    v-model="filterByRating"
                    class="custom-control-input"
                    id="customRatingSwitch"
                />
                <label
                    class="custom-control-label w-100 cursor-pointer"
                    for="customRatingSwitch"
                    >{{ __("Filter by rating") }}</label
                >
            </div>
            <div
                class="col-md-6 d-flex align-items-center"
                v-if="filterByRating"
            >
                <label class="mb-0 mr-1" for="minRating">{{ __("Min") }}</label>
                <input
                    class="form-control mr-2"
                    type="number"
                    min="0"
                    max="100"
                    id="minRating"
                    v-model="minRating"
                />
                <label class="mb-0 mr-1" for="maxRating">{{ __("Max") }}</label>
                <input
                    class="form-control"
                    type="number"
                    min="0"
                    max="100"
                    v-model="maxRating"
                />
            </div>
            <div class="col-md-12 pt-2" v-if="filterByRating">
                <p class="readable-text mb-0">
                    {{
                        __(
                            "Choose a rating between 0% and 100%. 5 star reviews are converted to 100%, which means that 1-star is 20%, 2-star is 40%, etc."
                        )
                    }}
                </p>
            </div>
        </div>
        <div class="row border border-rounded pill-shadow mb-3 mx-0 px-3">
            <div
                class="custom-control col-md-6 custom-switch d-flex align-items-center"
            >
                <input
                    type="checkbox"
                    v-model="filterByResponseStatus"
                    class="custom-control-input"
                    id="customResponseSwitch"
                />
                <label
                    class="custom-control-label w-100 cursor-pointer"
                    for="customResponseSwitch"
                    >{{ __("Filter by review status") }}</label
                >
            </div>
            <div class="col-md-6">
                <div v-if="filterByResponseStatus">
                    <element-selector
                        :title="'Type'"
                        :elements="availableStatus"
                        :initialSelected="selectedStatus"
                        v-model="selectedStatus"
                    />
                </div>
            </div>
            <div class="col-md-12 pt-2" v-if="filterByResponseStatus">
                <p class="readable-text mb-0">
                    {{
                        __(
                            "Choose a review status between With Response and Without Response"
                        )
                    }}
                </p>
            </div>
        </div>
        <div class="row border border-rounded pill-shadow mb-3 mx-0 px-3">
            <div
                class="custom-control col-md-6 custom-switch d-flex align-items-center"
            >
                <input
                    type="checkbox"
                    v-model="filterByRecency"
                    class="custom-control-input"
                    id="customRecencySwitch"
                />
                <label
                    class="custom-control-label w-100 cursor-pointer"
                    for="customRecencySwitch"
                    >{{ __("Filter by recency") }}</label
                >
            </div>
            <div
                class="col-md-6 d-flex align-items-center"
                v-if="filterByRecency"
            >
                <label class="mb-0 mr-1" for="reviewDays">{{
                    __("Days")
                }}</label>
                <input
                    class="form-control mr-2"
                    type="number"
                    min="0"
                    max="100"
                    id="reviewDays"
                    v-model="reviewDays"
                />
            </div>
            <div class="col-md-12 pt-2" v-if="filterByRecency">
                <p class="readable-text mb-0">
                    {{
                        __(
                            "Choose amount of days that need to have passed since a review was published"
                        )
                    }}
                </p>
            </div>
        </div>
        <div class="mb-3 col-md-12 col-8 ">
            <h5 class="header-case">
                {{ __("Enter Emails") }}
            </h5>
            <multi-text-input 
                :key="emailRerenderKey"
                :items="emails" 
                v-model="emails" 
                ref="email-input"
            />
        </div>
        <div>
            <label class="header-case" for="alertName">
                {{ __("Name") }}
            </label>
            <input
                id="alertName"
                type="text"
                class="form-control"
                placeholder="Enter a name for the alert"
                v-model="ruleName"
            />
        </div>
        <div class="d-flex justify-content-between pt-3">
            <div>
                <span v-if="estimating">{{ __("Estimating how many") }} </span>
                <span v-else-if="!estimating && estimate != null"
                    >{{ estimate }}
                </span>
                {{ __("reviews match.") }}
            </div>

            <button class="btn btn-primary" @click="action">
                {{ editing ? __("Edit alert") : __("Create alert") }}
            </button>
        </div>
    </div>
</template>

<script>
import ElementSelector from "../instagram/components/ElementSelector";
import MultiTextInput from "../generic/MultiTextInput.vue";
export default {
    props: {
        availableChannels: Array,
        sentimentGroups: Array,
        ruleToEdit: Object
    },

    components: {
        ElementSelector,
        MultiTextInput
    },

    watch: {
        currentPayload(newVal, oldVal) {
            this.getEstimate();
        }
    },

    mounted() {
        this.getEstimate();
    },

    data() {
        const rule = this.ruleToEdit || {}; 

        return {
            editing: this.ruleToEdit ? true : false, 
            filterBySource: rule.sources && rule.sources.length > 0,
            filterByRating: (rule.min_rating != null && rule.min_rating >= 0) || (rule.max_rating != null && rule.max_rating <= 100),
            filterBySentiment: rule.sentiment_group != null,
            filterByResponseStatus: rule.response_status != null,
            filterByRecency: rule.days_passed > 0,
            minRating: rule.min_rating || 0, 
            maxRating: rule.max_rating || 100, 
            selectedChannels: rule.sources || [], 
            selectedSentimentGroup: rule.sentiment_group || null, 
            selectedStatus: rule.response_status || null, 
            reviewDays: rule.days_passed || 1, 
            emails: rule.emails || [], 
            ruleName: rule.label || "", 
            estimating: false,
            estimate: 0,
            emailRerenderKey: 0, 
            formErrors: [],
            availableStatus: ["with_comments", "has_response", "no_comments"]
        };
    },

    computed: {
        currentPayload() {
            const payload = {};
            if (this.filterBySource) {
                payload.sources = this.selectedChannels;
            }
            if (this.filterBySentiment) {
                payload.sentimentGroup = this.selectedSentimentGroup;
            }
            if (this.filterByRating) {
                payload.minRating = this.minRating;
                payload.maxRating = this.maxRating;
            }
            if (this.filterByResponseStatus) {
                payload.responseStatus = this.selectedStatus;
            }
            if (this.filterByRecency) {
                payload.daysPassed = this.reviewDays;
            }
            if (this.ruleName.length > 2) {
                payload.label = this.ruleName;
            }
            if (this.emails.length > 0) {
                payload.emails = this.emails.map(email => {
                    return email.trim();
                });
            }
            if (this.editing) {
                payload.id = this.ruleToEdit.id;
            }

            return payload;
        }
    },

    methods: {
        reset() {
            this.minRating = 0;
            this.maxRating = 100;
            this.emails = [];
            this.selectedChannels = [];
            this.selectedSentimentGroup = null;
            this.filterBySource = false;
            this.filterByRating = false;
            this.filterBySentiment = false;
            this.minRating = 0;
            this.filterByResponseStatus = false;
            this.filterByRecency = false;
            this.reviewDays = 1;
            this.selectedStatus = null;
            this.ruleName = "";
            this.emailRerenderKey += 1;
            this.$refs?.["email-input"].clearElements();
        },

        getEstimate() {
            this.estimating = true;
            const payload = this.currentPayload;
            axios
                .post(
                    `/api/reviews/alerts/${
                        Spark.state.currentTeam.id
                    }/estimate`,
                    payload
                )
                .then(response => {
                    this.estimating = false;
                    this.estimate = response.data;
                });
        },

        validateFormData() {
            this.formErrors = [];
            if (this.filterBySource && this.selectedChannels.length < 1) {
                this.formErrors.push(
                    "Please choose a channel or disable filtering by channel."
                );
            }
            if (this.filterByRating && this.minRating > this.maxRating) {
                this.formErrors.push(
                    "The minimum rating cannot be lower than the maximum rating."
                );
            }
            if (this.filterBySentiment && this.selectedSentimentGroup == null) {
                this.formErrors.push(
                    "Please choose a sentiment group or disable filtering by sentiment group."
                );
            }
            if (this.filterByRecency && this.reviewDays < 0) {
                this.formErrors.push(
                    "Please select a valid review day. The review day cannot be lower than 0."
                );
            }
            if (this.filterByResponseStatus && this.selectedStatus == null) {
                this.formErrors.push(
                    "Please choose a review status or disable filtering by review status."
                );
            }
            if (this.emails.length == 0) {
                this.formErrors.push("Please enter at least one email.");
            }
            for (let email of this.emails) {
                if (email.length < 2 || !email.includes("@")) {
                    this.formErrors.push(`The email ${email} is not valid.`);
                }
            }
            if (this.ruleName.length < 2) {
                this.formErrors.push(
                    "Enter a label that's at least 2 characters long."
                );
            }

            return this.formErrors.length == 0;
        },

        action() {
            if (!this.validateFormData()) {
                return;
            }

            const payload = this.currentPayload;
            const createUrl = `/api/reviews/alerts/${Spark.state.currentTeam.id}`;
            const updateUrl = `${createUrl}/${this.ruleToEdit?.id}`;
            const url = this.editing ? updateUrl : createUrl;
            const requestType = this.editing ? 'put' : 'post'; 
            axios[requestType](url, payload)
                .then(response => {
                    const action = this.editing ? "updated" : "created";
                    const succuss = response.data === "success";
                    const title = succuss ? "Done!" : "Failed!";
                    const text = succuss ? `The alert rule has been ${action}.` : `The alert rule was not ${action}.`;
                    const icon = succuss ? "success" : "error";
                    swal(title, text, icon);

                    if (!this.editing && succuss) {
                        this.reset();
                    }
                });
        }
    }
};
</script>
